import { getPrimaryColor, JEJURO } from '@orcar/common-d2c';
import styled, { css } from 'styled-components';

export type InsuranceTier = {
  /** 사전예약 보험명(None/Normal/Perfect) */
  insuranceName: string;
  /** 카드 제목 (차량대여료 / 베이직 / 프리미엄) */
  label: string;
  variant: 'none' | 'basic' | 'premium';
  additionalPrice: number;
  /** 보장 상세 라인 (보상한도 / 단독사고 / 제한나이 / 운전경력 / 자기부담금 / 휴차보상료) */
  lines: {
    label: string;
    value: string;
    /** strong: 보상한도·단독사고 등 강조 / muted: 자기부담금 등 약하게 */
    emphasis?: 'strong' | 'muted';
  }[];
};

type InsuranceTierSelectorProps = {
  tiers: InsuranceTier[];
  /** 현재 선택된 보험명 */
  selected: string;
  onSelect: (insuranceName: string) => void;
  isMobile?: boolean;
  /** 선택 반영 중(사전예약 재생성) 여부 */
  loading?: boolean;
};

const ShieldOff = () => (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' aria-hidden>
    <path
      d='M12 3 5 5.5V11c0 4.2 2.9 7.6 7 9 4.1-1.4 7-4.8 7-9V5.5L12 3Z'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinejoin='round'
    />
    <path
      d='m4 4 16 16'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
    />
  </svg>
);

const Shield = ({ filled }: { filled?: boolean }) => (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' aria-hidden>
    <path
      d='M12 3 5 5.5V11c0 4.2 2.9 7.6 7 9 4.1-1.4 7-4.8 7-9V5.5L12 3Z'
      fill={filled ? 'currentColor' : 'none'}
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinejoin='round'
    />
    {filled && (
      <path
        d='m8.5 11.5 2.3 2.3L15 9.5'
        stroke='#fff'
        strokeWidth='1.7'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    )}
  </svg>
);

const TierIcon = ({ variant }: { variant: InsuranceTier['variant'] }) => {
  if (variant === 'none') return <ShieldOff />;
  if (variant === 'premium') return <Shield filled />;
  return <Shield />;
};

/**
 * 결제(상세) 페이지 보험 3단계 선택 카드.
 * 차량대여료(자차미가입) / 베이직(일반면책) / 프리미엄(고급면책) 중 선택하며,
 * 선택 시 상위(PaymentPage)에서 사전예약을 해당 보험으로 재생성해 가격을 갱신한다.
 */
const InsuranceTierSelector = ({
  tiers,
  selected,
  onSelect,
  isMobile,
  loading,
}: InsuranceTierSelectorProps) => {
  return (
    <Grid isMobile={isMobile}>
      {tiers.map((tier) => {
        const isSelected = tier.insuranceName === selected;
        return (
          <Tier
            key={tier.insuranceName}
            type='button'
            selected={isSelected}
            disabled={loading}
            onClick={() => !isSelected && onSelect(tier.insuranceName)}
          >
            <Head>
              <IconWrap variant={tier.variant} selected={isSelected}>
                <TierIcon variant={tier.variant} />
              </IconWrap>
              <TierName>{tier.label}</TierName>
              {isSelected && <SelectedDot aria-label='선택됨'>✓</SelectedDot>}
            </Head>
            <Lines>
              {tier.lines.map((line) => (
                <Line key={line.label} emphasis={line.emphasis}>
                  <b>{line.label}</b>
                  <span>{line.value}</span>
                </Line>
              ))}
            </Lines>
            <PriceArea>
              <span style={{ fontSize: 12, color: '#606870' }}>
                보험 추가금 · 선택 기간 전체
              </span>
              <Price>+{tier.additionalPrice.toLocaleString()}원</Price>
            </PriceArea>
          </Tier>
        );
      })}
    </Grid>
  );
};

export { InsuranceTierSelector };

const Grid = styled.div<{ isMobile?: boolean }>`
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : '1fr 1fr')};
  gap: 12px;
`;

const Tier = styled.button<{ selected?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 18px;
  text-align: left;
  border-radius: 14px;
  background: ${({ theme }) => theme.COLOR.WHITE};
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  ${({ selected, theme }) =>
    selected
      ? css`
          border: 2px solid ${getPrimaryColor(JEJURO)};
          box-shadow: 0 0 0 4px ${getPrimaryColor(JEJURO)}14;
        `
      : css`
          border: 1px solid ${theme.COLOR.GREY_05};
        `}

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconWrap = styled.span<{
  variant: InsuranceTier['variant'];
  selected?: boolean;
}>`
  display: inline-flex;
  color: ${({ variant, theme }) => {
    if (variant === 'none') return theme.COLOR.GREY_03;
    if (variant === 'premium') return '#E1A73E';
    return getPrimaryColor(JEJURO);
  }};
`;

const TierName = styled.span`
  ${({ theme }) => theme.FONT.H4};
  font-weight: 800;
`;

const SelectedDot = styled.span`
  margin-left: auto;
  display: inline-grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: ${getPrimaryColor(JEJURO)};
  color: #fff;
  font-size: 13px;
  font-weight: 800;
`;

const Lines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Line = styled.div<{ emphasis?: 'strong' | 'muted' }>`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  ${({ theme }) => theme.FONT.B3};
  color: ${({ theme, emphasis }) =>
    emphasis === 'muted' ? theme.COLOR.GREY_04 : theme.COLOR.GREY_02};
  line-height: 1.45;

  b {
    flex: 0 0 auto;
    font-weight: 700;
    color: ${({ theme, emphasis }) =>
      emphasis === 'strong'
        ? getPrimaryColor(JEJURO)
        : emphasis === 'muted'
          ? theme.COLOR.GREY_04
          : theme.COLOR.GREY_01};
  }

  span {
    flex: 1;
    min-width: 0;
    white-space: pre-line;
    word-break: keep-all;
    ${({ emphasis }) => emphasis === 'strong' && 'font-weight: 700;'}
    color: ${({ theme, emphasis }) =>
      emphasis === 'strong'
        ? getPrimaryColor(JEJURO)
        : emphasis === 'muted'
          ? theme.COLOR.GREY_04
          : theme.COLOR.GREY_02};
  }
`;

const PriceArea = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 6px;
  margin-top: auto;
  padding-top: 6px;
`;

const Price = styled.span`
  ${({ theme }) => theme.FONT.H3};
  font-weight: 800;
  color: ${getPrimaryColor(JEJURO)};
`;
